import { useRef, useEffect } from 'react'

import { useNavOverlay, NAV_OVERLAY_IDS } from 'Pods/UnifiedNavigation/Components/Navigation/Hooks/useNavOverlay'

const SCROLL_LOCK_CLASS = 'disable-page-scroll'

// Open state for the redesigned avatar dropdown, backed by the shared nav-overlay
// coordinator so opening it closes any other open overlay (and vice versa). Keeps
// the legacy close-on-outside-click and close-on-Escape behaviour on top.
function useUserMenu() {
  const { isOpen, open, close } = useNavOverlay(NAV_OVERLAY_IDS.USER_MENU)
  const wrapperRef = useRef(null)
  const isOpenRef = useRef(isOpen)

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  // Below md the user menu opens as a full-screen overlay (see
  // .unified-nav__dropdown--user), so lock body scroll like the hamburger menu does -
  // otherwise the page scrolls behind the dimming backdrop. Above md it is a small
  // floating card and the page must stay scrollable, so gate the lock on the viewport
  // and release it if the menu is left open while resizing up to the desktop layout.
  // Track our own lock so cleanup never strips a lock another overlay owns.
  useEffect(() => {
    if (!isOpen || typeof window.matchMedia !== 'function') {
      return undefined
    }

    const desktopQuery = window.matchMedia('(min-width: 960px)')
    let locked = false

    function syncScrollLock() {
      const shouldLock = !desktopQuery.matches

      if (shouldLock === locked) {
        return
      }

      locked = shouldLock
      document.body.classList.toggle(SCROLL_LOCK_CLASS, shouldLock)
    }

    syncScrollLock()
    desktopQuery.addEventListener('change', syncScrollLock)

    return () => {
      desktopQuery.removeEventListener('change', syncScrollLock)

      if (locked) {
        document.body.classList.remove(SCROLL_LOCK_CLASS)
      }
    }
  }, [isOpen])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  function handleClickOutside(e) {
    const currentRef = wrapperRef.current

    if (isOpenRef.current && currentRef && !currentRef.contains(e.target)) {
      close(NAV_OVERLAY_IDS.USER_MENU)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape' && isOpenRef.current) {
      close(NAV_OVERLAY_IDS.USER_MENU)
    }
  }

  function setIsOpen(value) {
    if (value) {
      open(NAV_OVERLAY_IDS.USER_MENU)
    } else {
      close(NAV_OVERLAY_IDS.USER_MENU)
    }
  }

  return { isOpen, setIsOpen, wrapperRef }
}

export { useUserMenu }
